<div class="top-bar">
  <!-- Autocomplete form aligned to the left -->
  <div class="autocomplete-container">
    <mat-form-field subscriptSizing="dynamic" appearance="fill">
      <mat-label>Select Dataset</mat-label>
      <input
        type="text"
        matInput
        [formControl]="datasetControl"
        [matAutocomplete]="autos"
        placeholder="Select a dataset"
      />
      <mat-autocomplete #autos="matAutocomplete" [panelWidth]="'auto'" class="dataset-autocomplete" (optionSelected)="onDatasetSelect($event)">
        <mat-option *ngFor="let dataset of filteredDatasets | async" [value]="dataset.datasetReferenceTable.datasetName">
          {{ dataset.datasetReferenceTable.datasetName }}
        </mat-option>
      </mat-autocomplete>
    </mat-form-field>
  </div>

  <!-- File Upload Section -->
  <!-- View dataset read-only banner -->
  <div *ngIf="isViewDataset" class="view-readonly-banner">
    <mat-icon>layers</mat-icon>
    <span>This is a view dataset (read-only). Data cannot be modified directly.</span>
  </div>

  <div class="file-upload-section" *ngIf="selectedDataset && !isViewDataset">
    <!-- Local File Upload -->
    <div class="file-option">
      <input
        type="file"
        #fileInput
        (change)="onFileSelected($event)"
        accept=".csv,.xlsx,.xls"
        style="display: none;"
      />
      <button
        mat-raised-button
        class="upload-btn"
        (click)="fileInput.click()"
        [disabled]="!selectedDataset">
        <mat-icon>upload_file</mat-icon>
        Choose Local File
      </button>
    </div>

    <!-- GCS File Selection -->
    <div class="file-option">
      <button
        mat-raised-button
        class="gcs-btn"
        (click)="openGCSFileBrowser()"
        [disabled]="!selectedDataset">
        <mat-icon>cloud</mat-icon>
        Choose from GCS
      </button>
    </div>

    <!-- Selected File Display -->
    <span class="selected-file" *ngIf="selectedFile">
      Local: {{ selectedFile.name }}
    </span>
    <span class="selected-file" *ngIf="selectedGCSFilePath">
      GCS: {{ selectedGCSFilePath.split('/').pop() }}
    </span>

    <!-- CSV Delimiter Selection (only show for CSV files) -->
    <div class="delimiter-option" *ngIf="(selectedFile?.name.toLowerCase().endsWith('.csv') || selectedGCSFilePath?.toLowerCase().endsWith('.csv'))">
      <mat-form-field appearance="outline" class="delimiter-field">
        <mat-label>CSV Delimiter</mat-label>
        <mat-select [(ngModel)]="csvDelimiter">
          <mat-option value=",">Comma (,)</mat-option>
          <mat-option value="|">Pipe (|)</mat-option>
          <mat-option value=";">Semicolon (;)</mat-option>
          <mat-option value="\t">Tab</mat-option>
          <mat-option value=":">Colon (:)</mat-option>
        </mat-select>
      </mat-form-field>
    </div>

    <!-- Import Button -->
    <button
      mat-raised-button
      class="import-btn"
      (click)="importFile()"
      [disabled]="(!selectedFile && !selectedGCSFilePath) || isImporting">
      <mat-icon>cloud_upload</mat-icon>
      {{ isImporting ? 'Importing...' : 'Import' }}
    </button>
  </div>

  <!-- Export Section -->
  <div class="export-section" *ngIf="selectedDataset">
    <mat-form-field appearance="outline" class="export-format-field" subscriptSizing="dynamic">
      <mat-label>Format</mat-label>
      <mat-select [(ngModel)]="exportFormat">
        <mat-option value="csv">CSV</mat-option>
        <mat-option value="json">JSON</mat-option>
      </mat-select>
    </mat-form-field>

    <button
      mat-raised-button
      class="export-btn"
      (click)="exportToGCS()"
      [disabled]="!selectedDataset || isExporting || data.length === 0">
      <mat-icon>cloud_download</mat-icon>
      {{ isExporting ? 'Exporting...' : 'Export to GCS' }}
    </button>
  </div>

  <!-- Buttons aligned to the right -->
  <div>
    <button mat-raised-button class="add-entry-btn" (click)="toggleEdit()" [disabled]="!selectedDataset" *ngIf="canEditData">
      {{!isEditing ? "Edit Data" : "Cancel"}}
    </button>
  </div>
</div>

<!-- Add New Entries Section (only shown when editing) -->
<div *ngIf="isEditing" class="add-section">
  <h3>Add New Entries</h3>
  <p-table [value]="newRows" [columns]="columns">
    <!-- Header -->
    <ng-template pTemplate="header" let-columns>
      <tr>
        <th *ngFor="let col of columns">{{ col.header }}</th>
        <th>Actions</th>
      </tr>
    </ng-template>

    <!-- Body -->
    <ng-template pTemplate="body" let-row let-rowIndex="rowIndex" let-columns="columns">
      <tr [class.validation-error]="getRowValidationError(rowIndex, true)">
        <td *ngFor="let col of columns">
          <!-- File Storage field -->
          <ng-container *ngIf="isFileStorageField(col) && isFieldEditable(col)">
            <div style="display: flex; align-items: center; gap: 8px;">
              <input
                [(ngModel)]="row[col.field]"
                pInputText
                placeholder="Select file..."
                [readonly]="true"
                style="flex: 1;" />
              <button mat-icon-button (click)="openFileBrowser(row, col)" title="Browse files">
                <mat-icon>folder_open</mat-icon>
              </button>
            </div>
          </ng-container>

          <!-- Reference field dropdown -->
          <ng-container *ngIf="col.referenceId && col.referenceId !== 0 && !isFileStorageField(col) && isFieldEditable(col)">
            <p-dropdown
              [options]="referencePicklist[col.field]"
              [(ngModel)]="row[col.field]"
              [showClear]="true"
              placeholder="Select a value"
              appendTo="body"
              optionLabel="label"
              optionValue="value"
              (onChange)="onReferenceValueChange(row, col)">
            </p-dropdown>
          </ng-container>

          <!-- Non-reference fields -->
          <ng-container *ngIf="(!col.referenceId || col.referenceId === 0) && isFieldEditable(col)">
            <!-- DateTime field -->
            <ng-container *ngIf="isDateTimeField(col)">
              <div class="datetime-input-container" style="display: flex; align-items: center; gap: 8px;">
                <mat-form-field appearance="outline" class="date-input" style="flex: 0 0 180px; max-width: 180px; margin: 0;">
                  <input matInput
                         [matDatepicker]="picker"
                         [(ngModel)]="row[col.field + '_date']"
                         (dateChange)="onDateChange($event, row, col)"
                         placeholder="MM/DD/YYYY"
>
                  <mat-datepicker-toggle matSuffix [for]="picker"></mat-datepicker-toggle>
                  <mat-datepicker #picker></mat-datepicker>
                </mat-form-field>

                <!-- Time Selection -->
                <p-dropdown
                  [options]="getHourOptions()"
                  [(ngModel)]="getTimeOptions(col.field, row).hour"
                  (onChange)="onTimeChange(row, col)"
                  placeholder="Hour"
                  optionLabel="label"
                  optionValue="value"
                  appendTo="body"
                  styleClass="datetime-dropdown"
                  [style]="{'width':'85px'}">
                </p-dropdown>

                <span style="line-height: 36px;">:</span>

                <p-dropdown
                  [options]="getMinuteOptions()"
                  [(ngModel)]="getTimeOptions(col.field, row).minute"
                  (onChange)="onTimeChange(row, col)"
                  placeholder="Min"
                  optionLabel="label"
                  optionValue="value"
                  appendTo="body"
                  styleClass="datetime-dropdown"
                  [style]="{'width':'85px'}">
                </p-dropdown>

                <p-dropdown
                  [options]="getPeriodOptions()"
                  [(ngModel)]="getTimeOptions(col.field, row).period"
                  (onChange)="onTimeChange(row, col)"
                  placeholder="AM/PM"
                  optionLabel="label"
                  optionValue="value"
                  appendTo="body"
                  styleClass="datetime-dropdown"
                  [style]="{'width':'90px'}">
                </p-dropdown>
              </div>
            </ng-container>

            <!-- Boolean field dropdown -->
            <ng-container *ngIf="isBooleanField(col)">
              <p-dropdown
                [options]="[{label: 'true', value: 'true'}, {label: 'false', value: 'false'}]"
                [(ngModel)]="row[col.field]"
                (ngModelChange)="onFieldValueChange(row, col)"
                [showClear]="true"
                placeholder="Select value"
                appendTo="body"
                optionLabel="label"
                optionValue="value">
              </p-dropdown>
            </ng-container>

            <!-- Regular input field -->
            <ng-container *ngIf="!isDateTimeField(col) && !isBooleanField(col)">
              <input
                [(ngModel)]="row[col.field]"
                (ngModelChange)="onFieldValueChange(row, col)"
                pInputText
                placeholder="Enter value" />
            </ng-container>
          </ng-container>

          <!-- Hidden/Non-editable fields -->
          <ng-container *ngIf="!isFieldEditable(col)">
            <input
              [value]="row[col.field]"
              pInputText
              [disabled]="true"
              class="disabled-field"
              placeholder="Auto-generated" />
          </ng-container>
        </td>
        <td>
          <button mat-icon-button (click)="removeNewRow(rowIndex)" [disabled]="newRows.length <= 1" title="Remove row">
            <mat-icon>delete</mat-icon>
          </button>
        </td>
      </tr>
      <!-- Validation error row -->
      <tr *ngIf="getRowValidationError(rowIndex, true)" class="error-row">
        <td [attr.colspan]="columns.length + 1" class="error-message">
          {{ getRowValidationError(rowIndex, true) }}
        </td>
      </tr>
    </ng-template>
  </p-table>

  <div class="add-section-buttons">
    <button mat-raised-button (click)="addNewRow()">Add Row</button>
    <button mat-raised-button (click)="submitNewEntries()">Submit New Entries</button>
  </div>
</div>

<!-- Filter Section -->
<div class="filter-section" *ngIf="selectedDataset && !isEditing">
  <div class="filter-header">
    <h3>Filter Data</h3>
    <div class="filter-actions">
      <button mat-raised-button (click)="addFilterRow()" class="add-filter-btn">
        <mat-icon>add</mat-icon>
        Add Filter
      </button>
      <button mat-raised-button (click)="applyFilters()" [disabled]="filterRows.length === 0" class="apply-filter-btn">
        <mat-icon>filter_alt</mat-icon>
        Apply Filters
      </button>
      <button mat-raised-button (click)="clearFilters()" [disabled]="!hasActiveFilters" class="clear-filter-btn">
        <mat-icon>clear</mat-icon>
        Clear Filters
      </button>
    </div>
  </div>

  <!-- Active filters indicator -->
  <div *ngIf="hasActiveFilters" class="active-filters-indicator">
    <mat-icon>filter_alt</mat-icon>
    <span>{{ Object.keys(activeFilters).length }} filter(s) active - Showing {{ totalEntries }} matching row(s)</span>
  </div>

  <!-- Filter rows -->
  <div class="filter-rows" *ngIf="filterRows.length > 0">
    <div class="filter-row" *ngFor="let filterRow of filterRows; let i = index">
      <mat-form-field appearance="outline" class="filter-column-field">
        <mat-label>Column</mat-label>
        <mat-select [(ngModel)]="filterRow.fieldName" (selectionChange)="onFilterFieldChange(filterRow)">
          <mat-option *ngFor="let col of getAvailableFilterColumns()" [value]="col.field">
            {{ col.header }}
          </mat-option>
        </mat-select>
      </mat-form-field>

      <mat-form-field appearance="outline" class="filter-match-mode-field" *ngIf="filterRow.fieldName && filterRow.availableMatchModes">
        <mat-label>Match Mode</mat-label>
        <mat-select [(ngModel)]="filterRow.matchMode" (selectionChange)="onMatchModeChange(filterRow)">
          <mat-option *ngFor="let mode of filterRow.availableMatchModes" [value]="mode.value">
            {{ mode.label }}
          </mat-option>
        </mat-select>
      </mat-form-field>

      <!-- DateTime filter value -->
      <ng-container *ngIf="isDateTimeFilterRow(filterRow)">
        <mat-form-field appearance="outline" class="filter-value-field">
          <mat-label>{{ filterRow.matchMode === 'between' ? 'From Date' : 'Date' }}</mat-label>
          <input matInput
                 [matDatepicker]="filterPicker1"
                 [(ngModel)]="filterRow.dateValue"
                 (dateChange)="onFilterDateChange(filterRow, false)"
                 placeholder="MM/DD/YYYY" />
          <mat-datepicker-toggle matSuffix [for]="filterPicker1"></mat-datepicker-toggle>
          <mat-datepicker #filterPicker1></mat-datepicker>
        </mat-form-field>

        <mat-form-field appearance="outline" class="filter-value-field" *ngIf="filterRow.matchMode === 'between'">
          <mat-label>To Date</mat-label>
          <input matInput
                 [matDatepicker]="filterPicker2"
                 [(ngModel)]="filterRow.dateValue2"
                 (dateChange)="onFilterDateChange(filterRow, true)"
                 placeholder="MM/DD/YYYY" />
          <mat-datepicker-toggle matSuffix [for]="filterPicker2"></mat-datepicker-toggle>
          <mat-datepicker #filterPicker2></mat-datepicker>
        </mat-form-field>
      </ng-container>

      <!-- Non-DateTime filter value -->
      <ng-container *ngIf="!isDateTimeFilterRow(filterRow)">
        <mat-form-field appearance="outline" class="filter-value-field">
          <mat-label>{{ filterRow.matchMode === 'between' ? 'From Value' : 'Value' }}</mat-label>
          <input matInput [(ngModel)]="filterRow.value" placeholder="Enter value" />
        </mat-form-field>

        <mat-form-field appearance="outline" class="filter-value-field" *ngIf="filterRow.matchMode === 'between'">
          <mat-label>To Value</mat-label>
          <input matInput [(ngModel)]="filterRow.value2" placeholder="Enter end value" />
        </mat-form-field>
      </ng-container>

      <button mat-icon-button (click)="removeFilterRow(i)" color="warn" class="remove-filter-btn">
        <mat-icon>delete</mat-icon>
      </button>
    </div>
  </div>
</div>

<!-- Columns & Group By Section -->
<div class="filter-section grouping-section" *ngIf="selectedDataset && !isEditing">
  <div class="filter-header">
    <h3>Columns &amp; Group By</h3>
    <div class="filter-actions">
      <button mat-raised-button (click)="addAggregationRow()" class="add-filter-btn">
        <mat-icon>add</mat-icon>
        Add Aggregation
      </button>
      <button mat-raised-button (click)="applyGroupBy()" [disabled]="groupByFields.length === 0" class="apply-filter-btn">
        <mat-icon>workspaces</mat-icon>
        Apply Group By
      </button>
      <button mat-raised-button (click)="clearGroupBy()" [disabled]="!isGroupedView && groupByFields.length === 0 && aggregationRows.length === 0" class="clear-filter-btn">
        <mat-icon>clear</mat-icon>
        Clear Group By
      </button>
    </div>
  </div>

  <!-- Active grouping indicator -->
  <div *ngIf="isGroupedView" class="active-filters-indicator">
    <mat-icon>workspaces</mat-icon>
    <span>
      Grouped by {{ groupByFields.length }} column(s) - showing {{ totalEntries }} group(s).
      Grouped results are read-only. Export to GCS exports these grouped rows.
    </span>
  </div>

  <!-- Column selection and group-by fields -->
  <div class="selection-row">
    <mat-form-field appearance="outline" class="columns-field">
      <mat-label>Columns</mat-label>
      <mat-select [(ngModel)]="selectedColumnFields" multiple (closed)="onSelectedColumnsChange()">
        <mat-option *ngFor="let col of getSelectableColumns()" [value]="col.field">
          {{ col.header }}
        </mat-option>
      </mat-select>
    </mat-form-field>

    <button mat-stroked-button (click)="selectAllColumns()" class="select-all-btn">
      All columns
    </button>

    <mat-form-field appearance="outline" class="group-by-field">
      <mat-label>Group By</mat-label>
      <mat-select [(ngModel)]="groupByFields" multiple>
        <mat-option *ngFor="let col of getGroupableColumns()" [value]="col.field">
          {{ col.header }}
        </mat-option>
      </mat-select>
    </mat-form-field>
  </div>

  <!-- Aggregation rows -->
  <div class="filter-rows" *ngIf="aggregationRows.length > 0">
    <div class="filter-row" *ngFor="let aggRow of aggregationRows; let i = index">
      <mat-form-field appearance="outline" class="filter-column-field">
        <mat-label>Column</mat-label>
        <mat-select [(ngModel)]="aggRow.fieldName" (selectionChange)="onAggregationFieldChange(aggRow)">
          <mat-option [value]="ROW_COUNT_FIELD">Row Count</mat-option>
          <mat-option *ngFor="let col of getGroupableColumns()" [value]="col.field">
            {{ col.header }}
          </mat-option>
        </mat-select>
      </mat-form-field>

      <mat-form-field appearance="outline" class="filter-match-mode-field">
        <mat-label>Aggregation</mat-label>
        <mat-select [(ngModel)]="aggRow.func">
          <mat-option *ngFor="let fn of getAggregationFunctionsFor(aggRow.fieldName)" [value]="fn.value">
            {{ fn.label }}
          </mat-option>
        </mat-select>
      </mat-form-field>

      <button mat-icon-button (click)="removeAggregationRow(i)" color="warn" class="remove-filter-btn">
        <mat-icon>delete</mat-icon>
      </button>
    </div>
  </div>
</div>

<!-- Existing Data Section -->
<div class="existing-data-section">
  <h3 *ngIf="isEditing">Existing Data - Click Update to save changes to individual rows</h3>

  <!-- Info message for lazy loading -->
  <div *ngIf="isLazyLoadingEnabled" class="info-message">
    <mat-icon>info</mat-icon>
    <span>Large dataset detected ({{ totalEntries }} rows). Loading data page by page for better performance.</span>
  </div>

  <p-table
    [value]="data"
    [columns]="displayColumns"
    [paginator]="true"
    [rows]="pageSize"
    [totalRecords]="totalEntries"
    [lazy]="isLazyLoadingEnabled"
    [first]="first"
    [loading]="isLoadingData"
    (onPage)="onPageChange($event)"
    (sortFunction)="sortColumn($event)">

    <!-- Header -->
    <ng-template pTemplate="header" let-columns>
      <tr>
        <th *ngFor="let col of columns" [pSortableColumn]="col.field">
          {{ col.header }}
          <p-sortIcon [field]="col.field"></p-sortIcon>
        </th>
        <th *ngIf="isEditing">Actions</th>
      </tr>
    </ng-template>

    <!-- Body -->
    <ng-template pTemplate="body" let-row let-rowIndex="rowIndex" let-columns="columns">
      <tr [class.validation-error]="getRowValidationError(rowIndex, false)">
        <td *ngFor="let col of columns">
          <!-- Editing mode -->
          <ng-container *ngIf="isEditing">
            <!-- File Storage field (editable) -->
            <ng-container *ngIf="isFileStorageField(col) && isFieldEditable(col, true)">
              <div style="display: flex; align-items: center; gap: 8px;">
                <input
                  [(ngModel)]="row[col.field]"
                  pInputText
                  placeholder="Select file..."
                  [readonly]="true"
                  style="flex: 1;" />
                <button mat-icon-button (click)="openFileBrowser(row, col)" title="Browse files">
                  <mat-icon>folder_open</mat-icon>
                </button>
              </div>
            </ng-container>

            <!-- Reference field dropdown (editable) -->
            <ng-container *ngIf="col.referenceId && col.referenceId !== 0 && !isFileStorageField(col) && isFieldEditable(col, true)">
              <p-dropdown
                [options]="referencePicklist[col.field]"
                [(ngModel)]="row[col.field]"
                [showClear]="true"
                placeholder="Select a value"
                appendTo="body"
                optionLabel="label"
                optionValue="value"
                (onChange)="onReferenceValueChange(row, col)">
              </p-dropdown>
            </ng-container>

            <!-- Non-reference fields (editable) -->
            <ng-container *ngIf="(!col.referenceId || col.referenceId === 0) && isFieldEditable(col, true)">
              <!-- DateTime field -->
              <ng-container *ngIf="isDateTimeField(col)">
                <div class="datetime-input-container" style="display: flex; align-items: center; gap: 8px;">
                  <mat-form-field appearance="outline" class="date-input" style="flex: 0 0 180px; max-width: 180px; margin: 0;">
                    <input matInput
                           [matDatepicker]="picker2"
                           [(ngModel)]="row[col.field + '_date']"
                           (dateChange)="onDateChange($event, row, col)"
                           placeholder="MM/DD/YYYY"
  >
                    <mat-datepicker-toggle matSuffix [for]="picker2"></mat-datepicker-toggle>
                    <mat-datepicker #picker2></mat-datepicker>
                  </mat-form-field>

                  <!-- Time Selection -->
                  <p-dropdown
                    [options]="getHourOptions()"
                    [(ngModel)]="getTimeOptions(col.field, row).hour"
                    (onChange)="onTimeChange(row, col)"
                    placeholder="Hour"
                    optionLabel="label"
                    optionValue="value"
                    appendTo="body"
                    styleClass="datetime-dropdown"
                    [style]="{'width':'85px'}">
                  </p-dropdown>

                  <span style="line-height: 36px;">:</span>

                  <p-dropdown
                    [options]="getMinuteOptions()"
                    [(ngModel)]="getTimeOptions(col.field, row).minute"
                    (onChange)="onTimeChange(row, col)"
                    placeholder="Min"
                    optionLabel="label"
                    optionValue="value"
                    appendTo="body"
                    styleClass="datetime-dropdown"
                    [style]="{'width':'85px'}">
                  </p-dropdown>

                  <p-dropdown
                    [options]="getPeriodOptions()"
                    [(ngModel)]="getTimeOptions(col.field, row).period"
                    (onChange)="onTimeChange(row, col)"
                    placeholder="AM/PM"
                    optionLabel="label"
                    optionValue="value"
                    appendTo="body"
                    styleClass="datetime-dropdown"
                    [style]="{'width':'90px'}">
                  </p-dropdown>
                </div>
                </ng-container>

              <!-- Boolean field dropdown -->
              <ng-container *ngIf="isBooleanField(col)">
                <p-dropdown
                  [options]="[{label: 'true', value: 'true'}, {label: 'false', value: 'false'}]"
                  [(ngModel)]="row[col.field]"
                  (ngModelChange)="onFieldValueChange(row, col)"
                  [showClear]="true"
                  placeholder="Select value"
                  appendTo="body"
                  optionLabel="label"
                  optionValue="value">
                </p-dropdown>
              </ng-container>

              <!-- Regular input field -->
              <ng-container *ngIf="!isDateTimeField(col) && !isBooleanField(col)">
                <input
                  [(ngModel)]="row[col.field]"
                  (ngModelChange)="onFieldValueChange(row, col)"
                  pInputText
                  placeholder="Enter value" />
              </ng-container>
            </ng-container>

            <!-- Hidden/Non-editable fields (in edit mode) -->
            <ng-container *ngIf="!isFieldEditable(col, true)">
              <input
                [value]="row[col.field]"
                pInputText
                [disabled]="true"
                class="disabled-field"
                [placeholder]="col.isPrimaryKey ? 'Primary Key (read-only)' : 'Auto-generated'" />
            </ng-container>
          </ng-container>

          <!-- View mode -->
          <ng-container *ngIf="!isEditing">
            <ng-container *ngIf="col.referenceId && col.referenceId !== 0">
              <span>{{ getDisplayValueForReference(col.field, row[col.field]) }}</span>
            </ng-container>
            <ng-container *ngIf="!col.referenceId || col.referenceId === 0">
              <span>{{ row[col.field] }}</span>
            </ng-container>
          </ng-container>
        </td>

        <!-- Actions column (only in edit mode) -->
        <td *ngIf="isEditing">
          <button mat-raised-button (click)="submitRowUpdate(row, rowIndex)" title="Update row">
            Update
          </button>
          <button mat-icon-button (click)="deleteExistingRow(rowIndex)" color="warn" title="Delete row">
            <mat-icon>delete</mat-icon>
          </button>
        </td>
      </tr>
      <!-- Validation error row -->
      <tr *ngIf="isEditing && getRowValidationError(rowIndex, false)" class="error-row">
        <td [attr.colspan]="columns.length + 1" class="error-message">
          {{ getRowValidationError(rowIndex, false) }}
        </td>
      </tr>
    </ng-template>
  </p-table>
</div>
